import { supabase } from "@/integrations/supabase/client";
import { getOrCreateDirectThread, sendSmsManualReply } from "@/lib/sms-api";

/**
 * Dispatch a Grok copilot "send from the CRM" text through the line the SERVER
 * resolved for this user (send_sms tool → resolveSendFromOptions): the tool
 * payload names the provider and, for Twilio, the exact twilio_numbers row.
 * One line → auto-chosen; several → Grok asked the user first. This function
 * never picks a number itself.
 *
 * History: this path used to be hardwired to the Twilio thread system with a
 * platform-number fallback — a Dialpad-only business (Ryson) texted a client
 * from ANOTHER business's line (2026-07-10). Provider detection then moved
 * here (Dialpad-if-connected); now the server resolves the line explicitly and
 * this just executes it. The legacy auto-detect below survives ONLY for stale
 * payloads that predate `send_provider`. Shared by BOTH copilot surfaces
 * (voice context + text widget) per the one-engine rule — never fork it.
 */
export async function sendCrmSms(opts: {
  leadId: string;
  businessId: string;
  message: string;
  /** Contact phone if the caller already has it; otherwise resolved from the lead. */
  phone?: string | null;
  /** Server-chosen line: "dialpad" = the user's own Dialpad; "twilio" = a specific number. */
  provider?: "dialpad" | "twilio" | null;
  /** twilio_numbers.id when provider is "twilio". */
  fromNumberId?: string | null;
}): Promise<void> {
  const message = (opts.message || "").trim();
  if (!opts.leadId || !opts.businessId || !message) {
    throw new Error("Nothing to send — missing a lead or message text.");
  }

  let provider = opts.provider ?? null;
  if (!provider) {
    // Legacy payload without a server-resolved line (stale bundle) — detect the
    // provider the way the in-panel dialer defaults: Dialpad connection wins.
    try {
      const { data } = await supabase.functions.invoke(
        `dialpad-oauth?action=status&scope=user&business_id=${opts.businessId}`,
        { method: "GET" },
      );
      provider = (data as { connected?: boolean } | null)?.connected ? "dialpad" : "twilio";
    } catch {
      provider = "twilio";
    }
  }

  if (provider === "dialpad") {
    // The Dialpad send needs the destination number (the Twilio path resolves it
    // server-side from the lead, but dialpad-oauth expects an explicit phone).
    let phone = (opts.phone || "").trim();
    if (!phone) {
      const { data: lead } = await supabase
        .from("leads")
        .select("current_phone, phone")
        .eq("id", opts.leadId)
        .eq("business_id", opts.businessId)
        .maybeSingle();
      phone = String(lead?.current_phone || lead?.phone || "").trim();
    }
    if (!phone) throw new Error("That contact has no phone number on file.");
    const { data, error } = await supabase.functions.invoke("dialpad-oauth?action=sms", {
      method: "POST",
      body: { action: "sms", business_id: opts.businessId, phone, text: message },
    });
    if (error) throw error;
    const err = (data as { error?: string; hint?: string } | null)?.error;
    if (err) throw new Error((data as { hint?: string })?.hint || err);
    return;
  }

  // Twilio path: the CRM SMS thread system. fromNumberId pins the exact line
  // the user chose; without it the server-side reply route resolves the
  // business's main line (never another business's — sendSMS refuses).
  const thread = await getOrCreateDirectThread(opts.leadId, opts.businessId);
  const threadId = String((thread as { id?: string }).id ?? "");
  if (!threadId) throw new Error("Couldn't open the text thread for this contact.");
  await sendSmsManualReply(threadId, message, opts.businessId, opts.fromNumberId || undefined);
}
